import { type FC } from 'react';

import MarketoForm from 'molecules/Form';
import Image from 'molecules/Image';
import RichText from 'molecules/RichText';

import DashboardMockup from 'components/DashboardMockup';

import { useBuilderReference } from 'utils/hooks/useBuilderReference';
import { useHideGlobalChrome } from 'utils/layout/useImmersiveChrome';

import type { DashboardBackdropProps } from 'components/DashboardBackdrop/config';
import type { ReactChildren } from 'types/global';

const DashboardBackdrop: FC<DashboardBackdropProps & ReactChildren> = ({
  heading,
  seoHeading,
  subheading,
  marketoForm,
  children,
}) => {
  useHideGlobalChrome();

  const formRef = marketoForm as FormReference | undefined;
  const preloadedForm = formRef?.value?.data as FormReference | undefined;
  const fetchedForm = useBuilderReference<FormReference>(
    preloadedForm ? undefined : formRef?.model,
    preloadedForm ? undefined : formRef?.id,
  );
  const formData = preloadedForm || fetchedForm || undefined;

  const hasHeader = Boolean(heading || subheading);
  const focalContent = formData ? <MarketoForm formData={formData} /> : children;

  return (
    <div className="relative flex min-h-dvh w-full flex-col overflow-hidden bg-gray-900">
      <div className="absolute inset-0 hidden md:block">
        <DashboardMockup />
      </div>

      <div className="absolute inset-0 bg-gray-900/70" aria-hidden />

      <div className="relative z-10 flex h-18 shrink-0 items-center justify-center px-4 md:hidden">
        <Image
          url="/prismatic-logo.svg"
          alt="Prismatic logo"
          width={140}
          height={29}
          noFill
          priority
          className="h-auto w-[140px] object-contain"
        />
      </div>

      <div className="relative flex flex-1 items-start justify-center px-4 pb-6 md:items-center md:p-10">
        <div className="w-full max-w-[832px] overflow-hidden rounded-lg bg-white">
          <div className="flex flex-col gap-5 p-6 md:min-h-[28rem] md:flex-row md:gap-0 md:p-0">
            {!heading && seoHeading && <h1 className="sr-only">{seoHeading}</h1>}
            {hasHeader && (
              <>
                <div className="flex flex-col gap-3 md:w-64 md:shrink-0 md:gap-5 md:py-12 md:pl-9 md:pr-6">
                  {heading && (
                    // This is the page's real heading: the component is a full-viewport
                    // hero (global chrome hidden) and the backdrop mockup is decorative,
                    // so the focal-card heading is the H1 crawlers should pick up.
                    <h1 className="font-roboto text-3xl font-medium leading-9 text-gray-900 md:text-4xl md:leading-10">
                      {heading}
                    </h1>
                  )}
                  {subheading && (
                    <RichText
                      text={subheading}
                      className="font-roboto text-md font-medium leading-6 text-gray-1000 md:text-xl md:leading-8 md:[&>p:not(:first-child)]:text-[18px] md:[&>p:not(:first-child)]:leading-7"
                    />
                  )}
                </div>
                <div className="hidden w-px self-stretch bg-gray-300 md:block" />
              </>
            )}

            <div className="marketo-form--flat flex min-w-0 flex-1 flex-col md:px-9 md:py-12">{focalContent}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardBackdrop;
